import { useCallback, useEffect, useState } from "react";

interface DeviceOrientation {
  alpha: number | null;
  beta: number | null;
  gamma: number | null;
}

interface DeviceOrientationResult {
  orientation: DeviceOrientation;
  isSupported: boolean;
  hasPermission: boolean | null;
  requestPermission: () => Promise<boolean>;
}

export const useDeviceOrientation = (): DeviceOrientationResult => {
  const [orientation, setOrientation] = useState<DeviceOrientation>({
    alpha: null,
    beta: null,
    gamma: null,
  });

  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isSupported, setIsSupported] = useState(false);

  const handleOrientation = useCallback((event: DeviceOrientationEvent) => {
    setOrientation({
      alpha: event.alpha,
      beta: event.beta,
      gamma: event.gamma,
    });
  }, []);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;

    try {
      if (typeof (DeviceOrientationEvent as any).requestPermission === "function") {

        const permission = await (DeviceOrientationEvent as any).requestPermission();
        const granted = permission === "granted";

        setHasPermission(granted);
        return granted;
      } else {

        setHasPermission(true);
        return true;
      }
    } catch (error) {

      setHasPermission(false);
      return false;
    }
  }, [isSupported]);

  useEffect(() => {
    const hasDeviceOrientationEvent = typeof DeviceOrientationEvent !== "undefined";
    const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;

    const isMobileDevice = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isDesktop = (navigator.platform.includes("Win") || navigator.platform.includes("Mac") || (navigator.platform.includes("Linux") && !navigator.platform.includes("arm"))) && !isMobileDevice;

    const isEmulator = isDesktop && navigator.userAgent.includes("Mobile") && !isMobileDevice;

    const supported = hasDeviceOrientationEvent && hasTouch && !isEmulator && !isDesktop;



    setIsSupported(supported);
  }, []);

  useEffect(() => {
    if (isSupported && hasPermission === null) {
      if (typeof (DeviceOrientationEvent as any).requestPermission !== "function") {
        setHasPermission(true);
      }
    }
  }, [isSupported, hasPermission]);

  useEffect(() => {
    if (hasPermission === true && isSupported) {
      window.addEventListener("deviceorientation", handleOrientation);
      return () => window.removeEventListener("deviceorientation", handleOrientation);
    }
  }, [hasPermission, isSupported]);

  return {
    orientation,
    isSupported,
    hasPermission,
    requestPermission,
  };
};
